import React, {Component} from 'react';
import {action, observable, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import {Button, FormInput, FormSelect} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import PageTitle from '../../../components/common/PageTitle';
import './style.scss';
import './reviewToVideo.scss';

const RATIOS = [
  {value: '9:16', label: '9:16 세로(릴스/숏츠)', w: 1080, h: 1920},
  {value: '1:1',  label: '1:1 정사각',         w: 1080, h: 1080},
  {value: '16:9', label: '16:9 가로',          w: 1920, h: 1080}
];

const PRESETS = [
  {bg: '#0F172A', fg: '#F1F5F9', accent: '#818CF8', name: '다크'},
  {bg: '#FFFFFF', fg: '#1F2937', accent: '#4A6CF7', name: '화이트'},
  {bg: 'linear-gradient(135deg, #FDE68A 0%, #FCA5A5 100%)', fg: '#7C2D12', accent: '#9A3412', name: '선셋'},
  {bg: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)', fg: '#E0E7FF', accent: '#FBBF24', name: '미드나잇'},
  {bg: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)', fg: '#1E3A8A', accent: '#1D4ED8', name: '오션'},
  {bg: 'linear-gradient(135deg, #A7F3D0 0%, #6EE7B7 100%)', fg: '#064E3B', accent: '#047857', name: '민트'}
];

const TRANSITIONS = [
  {value: 'fade', label: '페이드'},
  {value: 'slide', label: '슬라이드'},
  {value: 'zoom', label: '줌'},
  {value: 'none', label: '없음'}
];

const FONT_FAMILIES = [
  'Pretendard, "Apple SD Gothic Neo", "Noto Sans KR", sans-serif',
  '"Nanum Gothic", "Apple SD Gothic Neo", sans-serif',
  '"Nanum Myeongjo", serif',
  '"Gowun Dodum", sans-serif'
];

const DEFAULT_SCENE_DURATION = 3000;
const FPS = 30;

@inject(({teamUser}) => ({me: teamUser.me, isLogin: teamUser.isLogin}))
@inject(({review, common}) => ({
  getReviewList: review.getReviewList,
  base64UploadImages: common.base64UploadImages,
  addMyImage: common.addMyImage
}))
@observer
class ReviewToVideo extends Component {

  @observable store = {};

  // 리뷰
  @observable reviews = [];
  @observable reviewsLoading = false;
  @observable reviewsKeyword = '';
  @observable reviewsMinStar = 4;
  @observable photoOnly = false;
  @observable reviewsPage = 1;
  @observable reviewsTotalPages = 1;

  // 씬
  @observable scenes = [];                  // [{id, review, durationMs}]
  @observable activeSceneIdx = 0;
  @observable defaultDuration = DEFAULT_SCENE_DURATION;

  // 스타일
  @observable ratioVal = '9:16';
  @observable presetIdx = 0;
  @observable bgColor = PRESETS[0].bg;
  @observable fgColor = PRESETS[0].fg;
  @observable accentColor = PRESETS[0].accent;
  @observable fontFamily = FONT_FAMILIES[0];
  @observable transition = 'fade';

  // 콘텐츠 토글
  @observable showStars = true;
  @observable showName = true;
  @observable showProduct = true;
  @observable showLogo = true;
  @observable customLogo = '';

  // 재생 / 녹화
  @observable isPlaying = false;
  @observable isRecording = false;
  @observable currentTime = 0;             // ms
  @observable canvasReady = false;

  // BGM (음원) — 사용자 업로드 파일을 영상 재생/녹화/다운로드에 합성
  @observable audioFileName = '';
  @observable audioVolume = 0.7;           // 0 ~ 1
  @observable audioLoop = true;            // 음원이 영상보다 짧을 때 자동 반복 (기본 ON)
  // 비-observable 핸들 (DOM/Web Audio API 객체)
  _audioUrl = null;                        // Object URL (해제 필요)
  _audioEl = null;                         // HTMLAudioElement (preview & 녹화 source)
  _audioCtx = null;                        // AudioContext
  _audioSourceNode = null;                 // MediaElementAudioSourceNode (한 element당 1회만 생성 가능)
  _audioGainNode = null;                   // GainNode (볼륨 제어)
  _audioStreamDest = null;                 // MediaStreamAudioDestinationNode (녹화용)

  // mp4 변환 (브라우저가 mp4 직접 녹화를 지원하지 않을 때 ffmpeg.wasm 폴백)
  @observable isConverting = false;
  @observable conversionProgress = 0;       // 0~100
  @observable conversionStage = '';          // '라이브러리 로딩' | '변환 중'

  // refs/state
  canvasRef = React.createRef();
  areaRef = React.createRef();
  searchTimer = null;
  _rafId = null;
  _startTime = 0;
  _recorder = null;
  _chunks = [];
  _imgCache = {};
  _ffmpeg = null;                            // ffmpeg.wasm 인스턴스 (lazy)

  componentDidMount() {
    if (this.props.me) this.store = this.props.me.store ? this.props.me.store : {};
    this.loadReviews();
    // 캔버스 마운트 후 첫 프레임
    setTimeout(() => this.drawFrame(0), 0);
  }

  componentWillUnmount() {
    this.stop();
    if (this._recorder && this._recorder.state !== 'inactive') {
      try { this._recorder.stop(); } catch (e) {}
    }
    // 오디오 리소스 정리 (ObjectURL + AudioContext + Audio element)
    this.clearAudio();
  }

  componentDidUpdate() {
    if (!this.isPlaying && !this.isRecording) {
      this.drawFrame(this.currentTime);
    }
  }

  @computed get hasStore() { return !!(this.store && this.store._id); }
  @computed get currentRatio() { return RATIOS.find(r => r.value === this.ratioVal) || RATIOS[0]; }
  @computed get totalDuration() { return this.scenes.reduce((s, x) => s + x.durationMs, 0); }
  @computed get sceneReviewCounts() {
    const map = {};
    this.scenes.forEach(s => {
      const id = s.review && s.review._id;
      if (id) map[id] = (map[id] || 0) + 1;
    });
    return map;
  }
  @computed get currentScene() {
    let t = 0;
    for (let i = 0; i < this.scenes.length; i++) {
      if (this.currentTime < t + this.scenes[i].durationMs) {
        return {scene: this.scenes[i], idx: i, sceneTime: this.currentTime - t};
      }
      t += this.scenes[i].durationMs;
    }
    const last = this.scenes[this.scenes.length - 1];
    return last ? {scene: last, idx: this.scenes.length - 1, sceneTime: last.durationMs} : null;
  }

  // === 리뷰 로딩 ===
  @action loadReviews = async () => {
    if (!this.hasStore) return;
    this.reviewsLoading = true;
    try {
      const params = {
        store_id: this.store._id,
        shopNo: this.store.shopNo || 1,
        limit: 30,
        page: this.reviewsPage,
        sort: 'createdAt',
        searchParams: {
          displayAt: 'all',
          star: {
            all: this.reviewsMinStar === 0,
            star3: this.reviewsMinStar <= 3,
            star4: this.reviewsMinStar <= 4,
            star5: this.reviewsMinStar <= 5
          }
        }
      };
      if (this.reviewsKeyword) params.searchParams.keyword = this.reviewsKeyword;
      if (this.photoOnly) params.reviewType = 'media';
      const res = await this.props.getReviewList(params);
      if (res && Array.isArray(res.docs)) {
        this.reviews = res.docs;
        this.reviewsTotalPages = res.totalPages || 1;
      }
    } catch (e) { toast.error('리뷰 조회 실패'); }
    finally { this.reviewsLoading = false; }
  };

  @action onKwChange = (e) => {
    this.reviewsKeyword = e.target.value;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => { this.reviewsPage = 1; this.loadReviews(); }, 300);
  };
  @action setMinStar = (n) => { this.reviewsMinStar = n; this.reviewsPage = 1; this.loadReviews(); };
  @action togglePhotoOnly = () => { this.photoOnly = !this.photoOnly; this.reviewsPage = 1; this.loadReviews(); };
  @action goReviewsPage = (p) => { if (p < 1 || p > this.reviewsTotalPages) return; this.reviewsPage = p; this.loadReviews(); };

  // === 씬 관리 ===
  @action addScene = (review) => {
    this.scenes = [...this.scenes, {
      id: `scene-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      review,
      durationMs: this.defaultDuration
    }];
    this.activeSceneIdx = this.scenes.length - 1;
  };

  @action removeScene = (idx) => {
    this.scenes = this.scenes.filter((_, i) => i !== idx);
    if (this.activeSceneIdx >= this.scenes.length) this.activeSceneIdx = Math.max(0, this.scenes.length - 1);
    this.currentTime = 0;
  };

  @action moveScene = (idx, dir) => {
    const next = idx + dir;
    if (next < 0 || next >= this.scenes.length) return;
    const arr = this.scenes.slice();
    const tmp = arr[idx]; arr[idx] = arr[next]; arr[next] = tmp;
    this.scenes = arr;
    this.activeSceneIdx = next;
  };

  @action selectScene = (idx) => {
    this.activeSceneIdx = idx;
    // 시간 점프
    let t = 0;
    for (let i = 0; i < idx; i++) t += this.scenes[i].durationMs;
    this.currentTime = t;
  };

  @action setSceneDuration = (idx, ms) => {
    const s = this.scenes[idx];
    if (!s) return;
    s.durationMs = Math.max(500, Math.min(15000, parseInt(ms, 10) || 1000));
  };

  @action setDefaultDuration = (ms) => { this.defaultDuration = parseInt(ms, 10) || 3000; };

  @action setRatio = (v) => { this.ratioVal = v; };
  @action setPreset = (idx) => {
    this.presetIdx = idx;
    const p = PRESETS[idx];
    this.bgColor = p.bg;
    this.fgColor = p.fg;
    this.accentColor = p.accent;
  };

  // === 재생/녹화 ===
  // === BGM (음원) 관리 ===
  @action setAudioFile = (file) => {
    if (!file) return;
    // 기존 음원 정리
    this.clearAudio({ keepGraph: false });

    this.audioFileName = file.name || 'audio';
    this._audioUrl = URL.createObjectURL(file);

    const el = new Audio();
    el.src = this._audioUrl;
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    el.loop = this.audioLoop; // 음원이 영상보다 짧을 때 반복 여부 (사용자 토글)
    el.volume = this.audioVolume;
    this._audioEl = el;
  };

  @action setAudioLoop = (loop) => {
    this.audioLoop = !!loop;
    if (this._audioEl) {
      try { this._audioEl.loop = this.audioLoop; } catch (_) {}
    }
  };

  @action setAudioVolume = (v) => {
    const vol = Math.max(0, Math.min(1, v));
    this.audioVolume = vol;
    if (this._audioEl) this._audioEl.volume = vol;
    if (this._audioGainNode) {
      try { this._audioGainNode.gain.value = vol; } catch (_) {}
    }
  };

  @action clearAudio = ({ keepGraph = false } = {}) => {
    if (this._audioEl) {
      try { this._audioEl.pause(); } catch (_) {}
      this._audioEl.src = '';
      this._audioEl = null;
    }
    if (this._audioUrl) {
      try { URL.revokeObjectURL(this._audioUrl); } catch (_) {}
      this._audioUrl = null;
    }
    this.audioFileName = '';

    // Web Audio 그래프는 element 별로 묶이므로 element 교체 시 재생성 필요 → 정리
    if (!keepGraph) {
      this._audioSourceNode = null;
      this._audioStreamDest = null;
      this._audioGainNode = null;
      if (this._audioCtx) {
        try { this._audioCtx.close(); } catch (_) {}
        this._audioCtx = null;
      }
    }
  };

  // 음원 재생/일시정지 동기화 — currentTime(ms)을 audio element 의 currentTime(sec)에 매핑
  _syncAudioPlay = async () => {
    const el = this._audioEl;
    if (!el) return;
    try {
      el.currentTime = (this.currentTime || 0) / 1000;
      el.volume = this.audioVolume;
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {/* autoplay block 무시 */});
    } catch (_) {}
  };
  _syncAudioPause = () => {
    if (this._audioEl) {
      try { this._audioEl.pause(); } catch (_) {}
    }
  };
  _syncAudioSeek = () => {
    if (this._audioEl) {
      try { this._audioEl.currentTime = (this.currentTime || 0) / 1000; } catch (_) {}
    }
  };

  @action play = () => {
    if (this.scenes.length === 0) { toast.error('씬을 먼저 추가하세요.'); return; }
    if (this.currentTime >= this.totalDuration) this.currentTime = 0;
    this.isPlaying = true;
    this._startTime = performance.now() - this.currentTime;
    this._syncAudioPlay();
    this._tick();
  };

  @action stop = () => {
    this.isPlaying = false;
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    if (!this.isRecording) this._syncAudioPause();
  };

  @action seek = (ms) => {
    this.currentTime = Math.max(0, Math.min(this.totalDuration, ms));
    if (this.isPlaying) this._startTime = performance.now() - this.currentTime;
    this._syncAudioSeek();
    this.drawFrame(this.currentTime);
  };

  _tick = () => {
    if (!this.isPlaying && !this.isRecording) return;
    this.currentTime = performance.now() - this._startTime;
    if (this.currentTime >= this.totalDuration) {
      this.currentTime = this.totalDuration;
      this.drawFrame(this.currentTime);
      if (this.isRecording) { this.stopRecord(); }
      this.stop();
      this._syncAudioPause();
      return;
    }
    this.drawFrame(this.currentTime);
    this._rafId = requestAnimationFrame(this._tick);
  };

  // 음원이 있을 때 Web Audio 그래프 구성: <audio> → Source → Gain → (Destination[speaker] + StreamDest[recording])
  // 한 element 당 MediaElementAudioSourceNode 는 1번만 생성 가능하므로 캐시.
  _ensureAudioGraph = () => {
    if (!this._audioEl) return null;
    if (this._audioStreamDest && this._audioSourceNode) return this._audioStreamDest;
    try {
      const AudioCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtor) return null;
      if (!this._audioCtx) this._audioCtx = new AudioCtor();
      if (this._audioCtx.state === 'suspended') { try { this._audioCtx.resume(); } catch (_) {} }
      if (!this._audioSourceNode) {
        this._audioSourceNode = this._audioCtx.createMediaElementSource(this._audioEl);
      }
      if (!this._audioGainNode) {
        this._audioGainNode = this._audioCtx.createGain();
        this._audioGainNode.gain.value = this.audioVolume;
      }
      if (!this._audioStreamDest) {
        this._audioStreamDest = this._audioCtx.createMediaStreamDestination();
      }
      // 그래프 연결 (중복 connect 는 무해 — Web Audio 는 중복 가장자리를 만들지 않음)
      this._audioSourceNode.connect(this._audioGainNode);
      this._audioGainNode.connect(this._audioCtx.destination);   // 스피커 출력 (프리뷰)
      this._audioGainNode.connect(this._audioStreamDest);        // 녹화 트랙
      return this._audioStreamDest;
    } catch (e) {
      console.warn('[reviewToVideo] AudioContext 초기화 실패:', e);
      return null;
    }
  };

  @action startRecord = async () => {
    if (this.scenes.length === 0) { toast.error('씬을 먼저 추가하세요.'); return; }
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    if (typeof MediaRecorder === 'undefined') {
      toast.error('이 브라우저는 영상 녹화를 지원하지 않습니다.'); return;
    }
    try {
      const videoStream = canvas.captureStream(FPS);

      // 음원이 있으면 오디오 트랙 합성
      let stream = videoStream;
      let hasAudio = false;
      if (this._audioEl) {
        const audioStreamDest = this._ensureAudioGraph();
        if (audioStreamDest && audioStreamDest.stream && audioStreamDest.stream.getAudioTracks().length) {
          stream = new MediaStream([
            ...videoStream.getVideoTracks(),
            ...audioStreamDest.stream.getAudioTracks()
          ]);
          hasAudio = true;
        }
      }

      // mp4 우선 → 미지원이면 webm. mp4가 직접 녹화되면 변환 불필요(빠름)
      const mp4Candidates = ['video/mp4;codecs=h264', 'video/mp4;codecs=avc1.42E01E', 'video/mp4'];
      const webmCandidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
      const mime = [...mp4Candidates, ...webmCandidates]
        .find(m => MediaRecorder.isTypeSupported(m)) || 'video/webm';
      const isMp4Direct = mime.indexOf('mp4') !== -1;
      const recorderOpts = {mimeType: mime, videoBitsPerSecond: 8000000};
      if (hasAudio) recorderOpts.audioBitsPerSecond = 192000;
      this._recorder = new MediaRecorder(stream, recorderOpts);
      this._chunks = [];
      this._recorder.ondataavailable = (e) => { if (e.data.size > 0) this._chunks.push(e.data); };
      this._recorder.onstop = async () => {
        const recordedBlob = new Blob(this._chunks, {type: mime});
        if (isMp4Direct) {
          this._downloadVideo(recordedBlob, 'mp4');
          toast.success('MP4 영상이 다운로드되었습니다.');
          return;
        }
        // webm → mp4 변환 (ffmpeg.wasm)
        try {
          const mp4Blob = await this._convertWebmToMp4(recordedBlob, { hasAudio });
          this._downloadVideo(mp4Blob, 'mp4');
          toast.success('MP4 영상이 다운로드되었습니다.');
        } catch (err) {
          // 변환 실패 시 webm 그대로 다운로드 (영상 자체는 보존)
          console.warn('[reviewToVideo] mp4 변환 실패 — webm 으로 다운로드', err);
          this._downloadVideo(recordedBlob, 'webm');
          toast.error('MP4 변환에 실패하여 webm 으로 다운로드되었습니다.');
        }
      };
      this._recorder.start(100);
      this.isRecording = true;
      this.currentTime = 0;
      this._startTime = performance.now();
      // 녹화 시작 직후 오디오를 처음부터 재생 (audio element 동기화)
      this._syncAudioPlay();
      this._tick();
    } catch (e) {
      toast.error('녹화 시작 실패: ' + (e.message || e));
    }
  };

  // === mp4 변환 (ffmpeg.wasm UMD CDN, single-thread — SharedArrayBuffer 불필요) ===
  _loadFFmpegLib = () => {
    if (window.FFmpeg && typeof window.FFmpeg.createFFmpeg === 'function') return Promise.resolve();
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://unpkg.com/@ffmpeg/ffmpeg@0.11.6/dist/ffmpeg.min.js';
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error('ffmpeg 라이브러리 로드 실패'));
      document.head.appendChild(s);
    });
  };

  _getFFmpeg = async () => {
    if (this._ffmpeg && this._ffmpeg.isLoaded && this._ffmpeg.isLoaded()) return this._ffmpeg;
    await this._loadFFmpegLib();
    const {createFFmpeg} = window.FFmpeg;
    const ffmpeg = createFFmpeg({
      log: false,
      corePath: 'https://unpkg.com/@ffmpeg/core@0.11.0/dist/ffmpeg-core.js',
      progress: ({ratio}) => {
        this.conversionProgress = Math.max(0, Math.min(100, Math.round((ratio || 0) * 100)));
      }
    });
    await ffmpeg.load();
    this._ffmpeg = ffmpeg;
    return ffmpeg;
  };

  @action _convertWebmToMp4 = async (webmBlob, opts = {}) => {
    this.isConverting = true;
    this.conversionProgress = 0;
    this.conversionStage = '라이브러리 로딩 중';
    try {
      const ffmpeg = await this._getFFmpeg();
      this.conversionStage = '변환 중';
      const {fetchFile} = window.FFmpeg;
      ffmpeg.FS('writeFile', 'input.webm', await fetchFile(webmBlob));
      // 공통 옵션
      const args = [
        '-i', 'input.webm',
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-pix_fmt', 'yuv420p',
        '-movflags', '+faststart'
      ];
      // 오디오 트랙 인코딩 (Opus → AAC). 영상 길이에 맞춰 컷.
      if (opts.hasAudio) {
        args.push('-c:a', 'aac', '-b:a', '192k', '-shortest');
      } else {
        // 명시적으로 오디오 트랙 제외 (입력에 무음 트랙이 있어도 깔끔히 무시)
        args.push('-an');
      }
      args.push('output.mp4');
      await ffmpeg.run.apply(ffmpeg, args);
      const data = ffmpeg.FS('readFile', 'output.mp4');
      try { ffmpeg.FS('unlink', 'input.webm'); } catch (_) {}
      try { ffmpeg.FS('unlink', 'output.mp4'); } catch (_) {}
      return new Blob([data.buffer], {type: 'video/mp4'});
    } finally {
      this.isConverting = false;
      this.conversionProgress = 0;
      this.conversionStage = '';
    }
  };

  _downloadVideo = (blob, ext) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `review-video-${this.ratioVal.replace(':', 'x')}-${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  @action stopRecord = () => {
    this.isRecording = false;
    if (this._recorder && this._recorder.state !== 'inactive') {
      try { this._recorder.stop(); } catch (e) {}
    }
    this.stop();
  };

  // === 캔버스 그리기 ===
  drawFrame = (timeMs) => {
    const canvas = this.canvasRef.current;
    if (!canvas) return;
    const r = this.currentRatio;
    if (canvas.width !== r.w) canvas.width = r.w;
    if (canvas.height !== r.h) canvas.height = r.h;
    const ctx = canvas.getContext('2d');

    // 배경
    this.drawBackground(ctx, canvas);

    if (this.scenes.length === 0) {
      ctx.fillStyle = this.hexAlpha(this.fgColor, 0.4);
      ctx.font = `500 ${r.w * 0.04}px ${this.fontFamily}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('좌측에서 리뷰를 추가하세요', r.w / 2, r.h / 2);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      return;
    }

    // 현재 씬
    let acc = 0;
    let sceneIdx = 0, sceneTime = 0;
    for (let i = 0; i < this.scenes.length; i++) {
      if (timeMs < acc + this.scenes[i].durationMs) {
        sceneIdx = i; sceneTime = timeMs - acc; break;
      }
      acc += this.scenes[i].durationMs;
      sceneIdx = i; sceneTime = this.scenes[i].durationMs;
    }
    const scene = this.scenes[sceneIdx];
    if (!scene) return;

    // 다음 씬과의 전환 (마지막 0.4초)
    const FADE_MS = 400;
    const nextScene = this.scenes[sceneIdx + 1];
    if (nextScene && sceneTime > scene.durationMs - FADE_MS) {
      const t = (sceneTime - (scene.durationMs - FADE_MS)) / FADE_MS;
      // 현재 씬은 페이드 아웃
      ctx.save();
      ctx.globalAlpha = 1 - t;
      this.drawScene(ctx, scene, sceneTime);
      ctx.restore();
      // 다음 씬 페이드 인
      ctx.save();
      ctx.globalAlpha = t;
      if (this.transition === 'slide') ctx.translate(r.w * (1 - t), 0);
      else if (this.transition === 'zoom') {
        ctx.translate(r.w / 2, r.h / 2);
        ctx.scale(0.85 + 0.15 * t, 0.85 + 0.15 * t);
        ctx.translate(-r.w / 2, -r.h / 2);
      }
      this.drawScene(ctx, nextScene, 0);
      ctx.restore();
    } else {
      this.drawScene(ctx, scene, sceneTime);
    }

    // 진행 바 (오버레이로 그림 — 녹화에 포함됨)
    if (this.totalDuration > 0) {
      const progressH = Math.max(4, r.w * 0.005);
      const ratio = Math.min(1, timeMs / this.totalDuration);
      ctx.fillStyle = this.hexAlpha(this.fgColor, 0.18);
      ctx.fillRect(0, r.h - progressH, r.w, progressH);
      ctx.fillStyle = this.accentColor;
      ctx.fillRect(0, r.h - progressH, r.w * ratio, progressH);
    }

    // 핸들/로고
    if (this.showLogo) this.drawLogo(ctx, canvas);
  };

  drawBackground = (ctx, canvas) => {
    if (this.bgColor.startsWith('linear-gradient')) {
      const m = this.bgColor.match(/linear-gradient\([^,]+,\s*(#[0-9a-fA-F]+)[^,]*,\s*(#[0-9a-fA-F]+)/);
      if (m) {
        const g = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        g.addColorStop(0, m[1]); g.addColorStop(1, m[2]);
        ctx.fillStyle = g;
      } else ctx.fillStyle = '#FFFFFF';
    } else ctx.fillStyle = this.bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  drawScene = (ctx, scene, sceneTime) => {
    const r = this.currentRatio;
    const W = r.w, H = r.h;
    const review = scene.review;
    const FADE_IN = 350;
    const fadeIn = Math.min(1, sceneTime / FADE_IN);

    // 위쪽 이미지 (있으면 H의 50%)
    const imgUrls = this.getReviewImageUrls(review);
    const imgUrl = imgUrls[0];
    const photoH = imgUrl ? H * 0.5 : 0;
    if (imgUrl) {
      const img = this.loadImage(imgUrl);
      // 켄번스: 시간이 지남에 따라 약간 줌
      const t = sceneTime / scene.durationMs;
      const zoom = 1 + 0.06 * t;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, W, photoH);
      ctx.clip();
      if (img) {
        const ar = img.width / img.height;
        let dw = W * zoom, dh = dw / ar;
        if (dh < photoH * zoom) { dh = photoH * zoom; dw = dh * ar; }
        const dx = (W - dw) / 2;
        const dy = (photoH - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
      } else {
        ctx.fillStyle = this.hexAlpha(this.fgColor, 0.08);
        ctx.fillRect(0, 0, W, photoH);
      }
      ctx.restore();
      // 그라데이션 오버레이 (텍스트 가독성)
      const grad = ctx.createLinearGradient(0, photoH * 0.6, 0, photoH);
      grad.addColorStop(0, 'rgba(0,0,0,0)');
      grad.addColorStop(1, this.hexAlpha(this.bgColor.startsWith('linear-gradient') ? '#000000' : this.bgColor, 0.6));
      ctx.fillStyle = grad;
      ctx.fillRect(0, photoH * 0.6, W, photoH * 0.4);
    }

    // 본문 영역 (이미지 아래)
    const pad = W * 0.08;
    const innerW = W - pad * 2;
    let topY = (imgUrl ? photoH + pad * 0.6 : H * 0.18);
    let botY = H - pad * 1.2;

    // 페이드 인 (전체 텍스트 영역)
    ctx.save();
    ctx.globalAlpha = fadeIn;

    // 별점
    if (this.showStars) {
      const starSize = W * 0.045;
      this.drawStars(ctx, pad, topY + starSize / 2, starSize, review.goodsPt || 5, this.accentColor, this.hexAlpha(this.fgColor, 0.18));
      topY += starSize * 1.6;
    }

    // 본문 텍스트 (슬라이드 인)
    const slideX = (1 - fadeIn) * 30;
    const text = this.cleanText(review.contents);
    const trunc = text.length > 200 ? text.slice(0, 200) + '…' : text;
    // 작성자 / 상품 영역 예약
    let reservedBot = 0;
    const nameH = W * 0.028 * 1.4;
    const productH = W * 0.024 * 1.4;
    if (this.showName) reservedBot += nameH + pad * 0.25;
    if (this.showProduct && (review.orderProductName || (review.product_id && review.product_id.productName))) reservedBot += productH;
    const textBot = botY - reservedBot - pad * 0.3;
    const textH = Math.max(50, textBot - topY);
    const baseFs = W * 0.045;
    this.drawTextInRect(ctx, trunc,
      pad + slideX, topY, innerW, textH,
      {weight: '600', fontSize: baseFs, lineHeight: 1.5, color: this.fgColor, valign: 'top', align: 'left'});

    // 작성자
    if (this.showName) {
      ctx.fillStyle = this.hexAlpha(this.fgColor, 0.7);
      ctx.font = `500 ${W * 0.028}px ${this.fontFamily}`;
      ctx.textBaseline = 'top';
      ctx.fillText(`— ${this.maskName(review.writerName || '고객')} 님`, pad, botY - reservedBot);
    }
    // 상품명
    if (this.showProduct) {
      const productName = review.orderProductName || (review.product_id && review.product_id.productName) || '';
      if (productName) {
        ctx.fillStyle = this.hexAlpha(this.fgColor, 0.5);
        ctx.font = `500 ${W * 0.024}px ${this.fontFamily}`;
        const py = botY - reservedBot + (this.showName ? nameH + pad * 0.25 : 0);
        const txt = productName.length > 40 ? productName.slice(0, 40) + '…' : productName;
        ctx.fillText(txt, pad, py);
      }
    }
    ctx.restore();
  };

  drawLogo = (ctx, canvas) => {
    const W = canvas.width, H = canvas.height;
    const label = this.customLogo || (this.store && this.store.name) || '';
    if (!label) return;
    ctx.fillStyle = this.hexAlpha(this.fgColor, 0.5);
    ctx.font = `600 ${W * 0.022}px ${this.fontFamily}`;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'bottom';
    ctx.fillText('@' + label, W - W * 0.06, H - W * 0.04);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
  };

  // === 헬퍼 (reviewToInsta와 유사) ===
  cleanText = (s) => !s ? '' : String(s).replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  maskName = (n) => !n ? '' : (n.length <= 1 ? n : (n.length === 2 ? n[0] + '*' : n[0] + '*'.repeat(n.length - 2) + n[n.length - 1]));
  hexAlpha = (hex, a) => {
    if (!hex || hex[0] !== '#') return hex;
    const c = hex.replace('#', '');
    return `rgba(${parseInt(c.slice(0, 2), 16)}, ${parseInt(c.slice(2, 4), 16)}, ${parseInt(c.slice(4, 6), 16)}, ${a})`;
  };

  getReviewImageUrls = (rv) => {
    if (!rv) return [];
    const out = [];
    const push = (it) => {
      if (!it) return;
      if (typeof it === 'string') out.push(it);
      else if (it.url) out.push(it.url);
      else if (it.path) out.push(it.path);
    };
    if (Array.isArray(rv.thumbs)) rv.thumbs.forEach(push);
    if (out.length === 0 && Array.isArray(rv.images)) rv.images.forEach(push);
    if (out.length === 0 && Array.isArray(rv.platformImages)) rv.platformImages.forEach(push);
    return out.filter(Boolean);
  };

  proxyUrl = (url) => {
    if (!url) return url;
    try {
      const u = new URL(url, window.location.origin);
      if (u.origin === window.location.origin) return url;
      return `https://orpapi.onreview.co.kr/api/img-proxy?url=${encodeURIComponent(url)}`;
    } catch (e) { return url; }
  };

  loadImage = (url) => {
    if (!url) return null;
    if (this._imgCache[url] === 'loading' || this._imgCache[url] === 'error') return null;
    if (this._imgCache[url]) return this._imgCache[url];
    this._imgCache[url] = 'loading';
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { this._imgCache[url] = img; this.drawFrame(this.currentTime); };
    img.onerror = () => { this._imgCache[url] = 'error'; };
    img.src = this.proxyUrl(url);
    return null;
  };

  drawStars = (ctx, x, y, size, count, accent, dim) => {
    const gap = size * 0.2;
    for (let i = 0; i < 5; i++) {
      const cx = x + i * (size + gap) + size / 2;
      this.drawStar(ctx, cx, y, size / 2, i < count ? accent : dim);
    }
  };
  drawStar = (ctx, cx, cy, r, color) => {
    const spikes = 5; const outerR = r; const innerR = r * 0.45;
    let rot = Math.PI / 2 * 3; const step = Math.PI / spikes;
    ctx.beginPath(); ctx.moveTo(cx, cy - outerR);
    for (let i = 0; i < spikes; i++) {
      ctx.lineTo(cx + Math.cos(rot) * outerR, cy + Math.sin(rot) * outerR); rot += step;
      ctx.lineTo(cx + Math.cos(rot) * innerR, cy + Math.sin(rot) * innerR); rot += step;
    }
    ctx.closePath(); ctx.fillStyle = color; ctx.fill();
  };

  wrapText = (ctx, text, maxWidth) => {
    const chars = text.split('');
    const lines = [];
    let line = '';
    for (let i = 0; i < chars.length; i++) {
      const test = line + chars[i];
      if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = chars[i]; }
      else line = test;
    }
    if (line) lines.push(line);
    return lines;
  };

  drawTextInRect = (ctx, text, x, y, w, h, opts) => {
    if (!text || h <= 0) return 0;
    const o = opts || {};
    ctx.fillStyle = o.color || this.fgColor;
    ctx.font = `${o.weight || '500'} ${o.fontSize || 16}px ${this.fontFamily}`;
    ctx.textBaseline = 'top';
    ctx.textAlign = o.align || 'left';
    const ax = o.align === 'center' ? x + w / 2 : (o.align === 'right' ? x + w : x);
    const lh = (o.fontSize || 16) * (o.lineHeight || 1.5);
    const lines = this.wrapText(ctx, text, w);
    const max = Math.max(1, Math.floor(h / lh));
    const visible = lines.slice(0, max);
    if (lines.length > max) {
      let last = visible[visible.length - 1];
      while (ctx.measureText(last + '…').width > w && last.length > 1) last = last.slice(0, -1);
      visible[visible.length - 1] = last + '…';
    }
    let dy = y;
    if (o.valign === 'middle') dy = y + Math.max(0, (h - visible.length * lh) / 2);
    else if (o.valign === 'bottom') dy = y + Math.max(0, h - visible.length * lh);
    visible.forEach(line => { ctx.fillText(line, ax, dy); dy += lh; });
    ctx.textAlign = 'left';
  };

  formatTime = (ms) => {
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  truncate = (s, n) => !s ? '' : (s.length > n ? s.slice(0, n) + '…' : s);

  // === 렌더 ===
  render() {
    const r = this.currentRatio;
    return (
      <div className="main-content">
        <PageTitle/>
        <div className="widget-box rtv-page">

          <div className="rtv-header">
            <div className="rtv-header__title">
              <p>리뷰를 짧은 영상(릴스/숏츠)으로 변환합니다. 씬을 추가하고 재생/녹화하면 MP4 파일로 저장됩니다.</p>
            </div>
            <div className="rtv-header__cta">
              <Button theme={this.isPlaying ? 'warning' : 'light'}
                      disabled={this.scenes.length === 0 || this.isRecording || this.isConverting}
                      onClick={() => this.isPlaying ? this.stop() : this.play()}>
                <FeatherIcon icon={this.isPlaying ? 'pause' : 'play'} size="14"/>
                <span> {this.isPlaying ? '일시정지' : '재생'}</span>
              </Button>
              <Button theme={this.isRecording ? 'danger' : 'primary'}
                      disabled={this.scenes.length === 0 || this.isConverting}
                      onClick={() => this.isRecording ? this.stopRecord() : this.startRecord()}>
                <FeatherIcon icon={this.isRecording ? 'square' : 'video'} size="14"/>
                <span> {this.isRecording ? '녹화 중지' : '녹화 & 다운로드'}</span>
              </Button>
            </div>
          </div>

          <div className="rtv-layout">
            {/* 좌: 리뷰 목록 */}
            <div className="rtv-panel rtv-panel--reviews">
              <div className="rtv-panel__head">
                <FeatherIcon icon="message-square" size="14"/>
                <span>리뷰</span>
              </div>
              <div className="rtv-panel__body">
                <div className="rtv-search">
                  <FeatherIcon icon="search" size="14"/>
                  <FormInput placeholder="검색" value={this.reviewsKeyword} onChange={this.onKwChange}/>
                </div>
                <div className="rtv-starfilter">
                  {[5, 4, 3, 0].map(n => (
                    <button key={n}
                            className={`rtv-chip ${this.reviewsMinStar === n ? 'is-on' : ''}`}
                            onClick={() => this.setMinStar(n)}>
                      {n === 0 ? '전체' : `${n}★`}
                    </button>
                  ))}
                  <button className={`rtv-chip rtv-chip--photo ${this.photoOnly ? 'is-on' : ''}`}
                          onClick={this.togglePhotoOnly}
                          title="이미지 첨부 리뷰만">
                    <FeatherIcon icon="camera" size="11"/> 포토
                  </button>
                </div>
                {this.reviewsLoading ? (
                  <div className="rtv-empty"><FeatherIcon icon="loader" size="28"/></div>
                ) : this.reviews.length === 0 ? (
                  <div className="rtv-empty"><FeatherIcon icon="inbox" size="36"/><div>리뷰가 없습니다</div></div>
                ) : (
                  <div className="rtv-rlist">
                    {this.reviews.map(rv => {
                      const imgUrls = this.getReviewImageUrls(rv);
                      const thumbs = imgUrls.slice(0, 3);
                      const addedCount = this.sceneReviewCounts[rv._id] || 0;
                      const isAdded = addedCount > 0;
                      return (
                        <div key={rv._id} className={`rtv-rcard ${isAdded ? 'is-added' : ''}`}>
                          {isAdded && (
                            <span className="rtv-rcard__addedbadge" title={`${addedCount}회 추가됨`}>
                              <FeatherIcon icon="check" size="10"/> {addedCount > 1 ? `×${addedCount}` : ''}
                            </span>
                          )}
                          <div className="rtv-rcard__head">
                            <span className="rtv-rcard__star">★ {rv.goodsPt || 5}</span>
                            <span className="rtv-rcard__name">{this.maskName(rv.writerName || '고객')}</span>
                            {imgUrls.length > 0 && (
                              <span className="rtv-rcard__photoflag" title="포토리뷰">
                                <FeatherIcon icon="camera" size="10"/> {imgUrls.length}
                              </span>
                            )}
                          </div>
                          {thumbs.length > 0 && (
                            <div className="rtv-rcard__thumbs">
                              {thumbs.map((t, i) => (
                                <span key={i} className="rtv-rcard__thumb" style={{backgroundImage: `url("${t}")`}}/>
                              ))}
                            </div>
                          )}
                          <div className="rtv-rcard__text">{this.truncate(this.cleanText(rv.contents), 90)}</div>
                          <button className={`rtv-rcard__add ${isAdded ? 'is-added' : ''}`} onClick={() => this.addScene(rv)}>
                            <FeatherIcon icon={isAdded ? 'plus-circle' : 'plus'} size="12"/>
                            {isAdded ? ' 씬 추가 +1' : ' 씬 추가'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {this.reviewsTotalPages > 1 && (
                  <div className="rtv-pager">
                    <Button size="sm" theme="light" disabled={this.reviewsPage <= 1} onClick={() => this.goReviewsPage(this.reviewsPage - 1)}>
                      <FeatherIcon icon="chevron-left" size="14"/>
                    </Button>
                    <span>{this.reviewsPage} / {this.reviewsTotalPages}</span>
                    <Button size="sm" theme="light" disabled={this.reviewsPage >= this.reviewsTotalPages} onClick={() => this.goReviewsPage(this.reviewsPage + 1)}>
                      <FeatherIcon icon="chevron-right" size="14"/>
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* 중: 캔버스 */}
            <div className="rtv-panel rtv-panel--canvas">
              <div className="rtv-panel__head">
                <FeatherIcon icon="film" size="14"/>
                <span>미리보기</span>
                <span className="rtv-panel__sub">{r.label} · {r.w}×{r.h} · {this.formatTime(this.totalDuration)}</span>
              </div>
              <div className="rtv-panel__body rtv-canvasarea" ref={this.areaRef}>
                <div className={`rtv-canvasframe rtv-canvasframe--${this.ratioVal.replace(':', 'x')}`}>
                  <canvas ref={this.canvasRef}/>
                  {this.isRecording && (
                    <div className="rtv-recording">
                      <span className="rtv-recording__dot"/> REC
                    </div>
                  )}
                  {this.isConverting && (
                    <div className="rtv-converting">
                      <FeatherIcon icon="loader" size="14" className="rtv-converting__spin"/>
                      <span>MP4 {this.conversionStage}{this.conversionProgress > 0 ? ` ${this.conversionProgress}%` : ''}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="rtv-timeline">
                <div className="rtv-timeline__time">{this.formatTime(this.currentTime)} / {this.formatTime(this.totalDuration)}</div>
                <input type="range" min="0" max={this.totalDuration || 1} step="50"
                       value={this.currentTime}
                       onChange={(e) => this.seek(parseInt(e.target.value, 10))}
                       disabled={this.isRecording || this.isConverting}/>
                <div className="rtv-scenestrip">
                  {this.scenes.map((s, i) => {
                    const ratio = this.totalDuration > 0 ? s.durationMs / this.totalDuration : 0;
                    return (
                      <div key={s.id}
                           className={`rtv-scenebar ${i === this.activeSceneIdx ? 'is-on' : ''}`}
                           style={{flexGrow: ratio || 1}}
                           onClick={() => this.selectScene(i)}>
                        <span>{i + 1}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 우: 씬 + 스타일 */}
            <div className="rtv-panel rtv-panel--style">
              <div className="rtv-panel__head">
                <FeatherIcon icon="layers" size="14"/>
                <span>씬 & 스타일</span>
              </div>
              <div className="rtv-panel__body">
                <div className="rtv-section">
                  <div className="rtv-section__label">씬 목록 ({this.scenes.length}개)</div>
                  {this.scenes.length === 0 ? (
                    <div className="rtv-help">좌측 리뷰의 "씬 추가" 버튼을 누르세요.</div>
                  ) : (
                    <div className="rtv-scenelist">
                      {this.scenes.map((s, i) => (
                        <div key={s.id} className={`rtv-sceneitem ${i === this.activeSceneIdx ? 'is-on' : ''}`}
                             onClick={() => this.selectScene(i)}>
                          <span className="rtv-sceneitem__num">{i + 1}</span>
                          <div className="rtv-sceneitem__info">
                            <div className="rtv-sceneitem__name">{this.maskName(s.review.writerName || '고객')}</div>
                            <div className="rtv-sceneitem__text">{this.truncate(this.cleanText(s.review.contents), 40)}</div>
                          </div>
                          <div className="rtv-sceneitem__actions" onClick={(e) => e.stopPropagation()}>
                            <input type="number" min="500" max="15000" step="500"
                                   value={s.durationMs}
                                   onChange={(e) => this.setSceneDuration(i, e.target.value)}
                                   title="재생 시간(ms)"/>
                            <button onClick={() => this.moveScene(i, -1)} disabled={i === 0} title="위로">
                              <FeatherIcon icon="arrow-up" size="11"/>
                            </button>
                            <button onClick={() => this.moveScene(i, 1)} disabled={i === this.scenes.length - 1} title="아래로">
                              <FeatherIcon icon="arrow-down" size="11"/>
                            </button>
                            <button className="is-danger" onClick={() => this.removeScene(i)} title="삭제">
                              <FeatherIcon icon="trash-2" size="11"/>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rtv-section">
                  <div className="rtv-section__label">비율</div>
                  <div className="rtv-ratiochips">
                    {RATIOS.map(rt => (
                      <button key={rt.value}
                              className={`rtv-chip ${this.ratioVal === rt.value ? 'is-on' : ''}`}
                              onClick={() => this.setRatio(rt.value)}>
                        {rt.value}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rtv-section">
                  <div className="rtv-section__label">컬러 프리셋</div>
                  <div className="rtv-presetgrid">
                    {PRESETS.map((p, i) => (
                      <button key={i}
                              className={`rtv-preset ${this.presetIdx === i ? 'is-on' : ''}`}
                              style={{background: p.bg}}
                              onClick={() => this.setPreset(i)}
                              title={p.name}>
                        <span className="rtv-preset__chip" style={{background: p.accent}}/>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rtv-section">
                  <div className="rtv-section__label">전환 효과</div>
                  <FormSelect value={this.transition} onChange={(e) => { this.transition = e.target.value; }}>
                    {TRANSITIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </FormSelect>
                </div>

                <div className="rtv-section">
                  <div className="rtv-section__label">기본 씬 길이 (ms)</div>
                  <FormInput type="number" min="500" max="15000" step="500"
                             value={this.defaultDuration}
                             onChange={(e) => this.setDefaultDuration(e.target.value)}/>
                  <div className="rtv-help">새 씬 추가시 적용되는 기본 시간 (개별 씬에서 변경 가능)</div>
                </div>

                {/* ===== BGM (음원) ===== */}
                <div className="rtv-section">
                  <div className="rtv-section__label">
                    <FeatherIcon icon="music" size="12" style={{verticalAlign:'middle', marginRight:4}}/>
                    배경 음원 (BGM)
                  </div>
                  {!this.audioFileName ? (
                    <label style={{
                      display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                      padding:'10px 12px', border:'1.5px dashed #D1D5DB', borderRadius:6,
                      background:'#F9FAFB', cursor:'pointer', fontSize:12.5, color:'#6B7280'
                    }}>
                      <FeatherIcon icon="upload" size="13"/>
                      <span>음원 파일 선택 (MP3, M4A, WAV)</span>
                      <input
                        type="file"
                        accept="audio/*"
                        style={{display:'none'}}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) this.setAudioFile(f);
                          e.target.value = ''; // 같은 파일 재선택 허용
                        }}/>
                    </label>
                  ) : (
                    <div style={{
                      display:'flex', alignItems:'center', gap:8,
                      padding:'8px 10px', background:'#F0F9FF',
                      border:'1px solid #BAE6FD', borderRadius:6
                    }}>
                      <FeatherIcon icon="music" size="14" style={{color:'#0369A1', flexShrink:0}}/>
                      <span title={this.audioFileName} style={{
                        flex:1, minWidth:0, fontSize:12, color:'#0C4A6E',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'
                      }}>{this.audioFileName}</span>
                      <button
                        type="button"
                        onClick={() => this.clearAudio()}
                        title="음원 제거"
                        style={{
                          background:'transparent', border:'none', cursor:'pointer',
                          padding:2, color:'#9CA3AF', display:'inline-flex'
                        }}>
                        <FeatherIcon icon="x" size="14"/>
                      </button>
                    </div>
                  )}
                  <div style={{marginTop:10, display:'flex', alignItems:'center', gap:8}}>
                    <FeatherIcon icon={this.audioVolume === 0 ? 'volume-x' : (this.audioVolume < 0.5 ? 'volume-1' : 'volume-2')} size="13" style={{color:'#6B7280'}}/>
                    <input
                      type="range" min="0" max="100" step="1"
                      value={Math.round(this.audioVolume * 100)}
                      onChange={(e) => this.setAudioVolume(parseInt(e.target.value, 10) / 100)}
                      style={{flex:1}}
                      disabled={!this.audioFileName}
                      title="볼륨"/>
                    <span style={{fontSize:11, color:'#6B7280', width:30, textAlign:'right'}}>
                      {Math.round(this.audioVolume * 100)}
                    </span>
                  </div>
                  <label style={{
                    display:'inline-flex', alignItems:'center', gap:6,
                    marginTop:8, fontSize:12, color:'#374151', cursor:'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={this.audioLoop}
                      onChange={(e) => this.setAudioLoop(e.target.checked)}/>
                    <FeatherIcon icon="repeat" size="12" style={{color: this.audioLoop ? '#10B981' : '#9CA3AF'}}/>
                    음원이 짧을 때 반복 재생
                  </label>
                  <div className="rtv-help">
                    {this.audioLoop
                      ? '음원이 영상보다 짧으면 자동 반복됩니다. 길면 영상 길이에 맞춰 잘립니다.'
                      : '음원이 영상보다 짧으면 그 시점부터 무음으로 진행됩니다.'}
                  </div>
                </div>

                <div className="rtv-section">
                  <div className="rtv-section__label">표시 항목</div>
                  <div className="rtv-toggles">
                    <label><input type="checkbox" checked={this.showStars} onChange={(e) => { this.showStars = e.target.checked; }}/> 별점</label>
                    <label><input type="checkbox" checked={this.showName} onChange={(e) => { this.showName = e.target.checked; }}/> 작성자</label>
                    <label><input type="checkbox" checked={this.showProduct} onChange={(e) => { this.showProduct = e.target.checked; }}/> 상품명</label>
                    <label><input type="checkbox" checked={this.showLogo} onChange={(e) => { this.showLogo = e.target.checked; }}/> 핸들/로고</label>
                  </div>
                  <FormInput placeholder="@핸들 (비우면 스토어명)" value={this.customLogo}
                             onChange={(e) => { this.customLogo = e.target.value; }}/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ReviewToVideo;
